// Б.2.d: Фиксер — два эффекта.
//
// 1) Триггер начала каждого хода: +0/1/0 (+1 Броня) Фиксеру.
//    Стак без потолка. Срабатывает в Ф1 после тяги обоими, начиная с
//    хода ПОСЛЕ выставления.
//
// 2) Приказ при выставлении (отличается по линии):
//    - 1-я линия: «снять Модификацию с любого союзного юнита и передать
//      её другому союзному юниту».
//    - 2-я линия: «передвинуть любого юнита на поле».
//      Свой юнит — между линиями своей стороны, вражеский — между линиями
//      вражеской стороны.
//
// Initiation = 0 → Фиксер не спит. Приказ срабатывает на Ф3 размещения
// через runUnitOrdersOnReveal.
//
// orderOpts — это структура, которую заполняет UI при размещении в Ф2:
//   1-я линия: { sourceInstanceId, sourceModIndex, targetInstanceId }
//   2-я линия: { unitInstanceId, line, slot }
// Для headless-тестов и бота: если opts пустой, выбирается дефолт
// (первый валидный) либо Приказ пропускается.

import { attachMod, detachMod } from './modifications.js';

export const CARD_ID = 'syn_fixer';

// === Триггер 1: пассивный хил +1 hp каждый ход (по тексту карты v6) ===
// Этап 56-fix: было ошибочно +1 armor — карта говорит «лечит сам себя на 1 hp».
// Вызывается из onTurnStart для каждого Фиксера на поле.
// Не работает: спящий, мёртвый, выставлен в этот же ход (placedTurn === currentTurn).
export function fikserOnTurnStart(state, ownerIdx, fikser) {
  if (!fikser.isAlive()) return;
  if (fikser.isSleeping(state.currentTurn)) return;
  // Со следующего хода после выставления.
  if (fikser.placedTurn != null && fikser.placedTurn >= state.currentTurn) return;
  if (fikser.current.hp >= fikser.base.hp) return; // не выше max
  const before = fikser.current.hp;
  fikser.current.hp = Math.min(fikser.current.hp + 1, fikser.base.hp);
  state.log.add(
    `🔧 ${fikser.name_ru} [${state.players[ownerIdx].name}]: +1 hp (пассив) ` +
    `→ HP ${before}→${fikser.current.hp}`
  );
}

// === Приказ: маршрут по линии ===
export function fikserOrder(state, ownerIdx, fikser, opts = {}) {
  if (!fikser.isAlive()) return;
  // Б.2.d-fix3: явный skip через UI (нажат Пасс на picker'е).
  if (opts && opts.skipped) {
    state.log.add(`🔧 ${fikser.name_ru} [${state.players[ownerIdx].name}]: Приказ пропущен пользователем`);
    return;
  }
  // Аудит-фикс под v6: Приказ = ТОЛЬКО перенос Мода, на ЛЮБОЙ линии.
  // Старый Б.2.d-эффект «движение юнита на 2-й линии» удалён — его НЕТ в тексте
  // карты v6 (ability_text.order = «перенеси 1 Мод…»). fikserOrderMove ниже
  // больше не вызывается (оставлен мёртвым, чтобы не трогать смежное).
  fikserOrderTransfer(state, ownerIdx, fikser, opts);
}

// === 1-я линия: перенос Мода ===
function fikserOrderTransfer(state, ownerIdx, fikser, opts) {
  const p = state.players[ownerIdx];
  const allies = p.unitsOnField();
  // Источник: указан UI или дефолт = первый союзник с ≥1 Модом.
  let source = null;
  let sourceModIndex = 0;
  if (opts.sourceInstanceId != null) {
    source = allies.find(u => u.instanceId === opts.sourceInstanceId) || null;
    sourceModIndex = opts.sourceModIndex ?? 0;
  } else {
    source = allies.find(u => (u.modifications?.length ?? 0) > 0) || null;
  }
  if (!source || (source.modifications?.length ?? 0) === 0) {
    state.log.add(`🔧 ${fikser.name_ru} [${p.name}]: Приказ пропущен — нет Модов на союзниках`);
    return;
  }
  if (sourceModIndex < 0 || sourceModIndex >= source.modifications.length) sourceModIndex = 0;
  const modBeingMoved = source.modifications[sourceModIndex];
  // Цель: указана UI или дефолт = первый союзник кроме источника.
  let target = null;
  if (opts.targetInstanceId != null) {
    target = allies.find(u => u.instanceId === opts.targetInstanceId) || null;
  } else {
    target = allies.find(u => u.instanceId !== source.instanceId) || null;
  }
  if (!target || target.instanceId === source.instanceId) {
    state.log.add(`🔧 ${fikser.name_ru} [${p.name}]: Приказ пропущен — нет валидной цели для переноса`);
    return;
  }
  state.log.add(
    `🔧 ${fikser.name_ru} [${p.name}]: переносит Мод "${modBeingMoved.name_ru}" ` +
    `с ${source.name_ru} → ${target.name_ru}`
  );
  // Снять с источника (detachMod откатывает дельту).
  const detRes = detachMod(state, source, sourceModIndex);
  if (!detRes.ok) {
    state.log.add(`🔧 ${fikser.name_ru} [${p.name}]: detach не удался (${detRes.reason})`);
    return;
  }
  // Прицепить к цели (attachMod применяет эффект). Без isPlayed — это перенос
  // триггером, не розыгрыш карты. Хирург не реагирует.
  attachMod(state, target, {
    id: modBeingMoved.id,
    name_ru: modBeingMoved.name_ru
  }, {
    cardId: CARD_ID, ownerIdx, transfer: true
  });
}

// === 2-я линия: передвижение юнита ===
function fikserOrderMove(state, ownerIdx, fikser, opts) {
  const p = state.players[ownerIdx];
  // Найти юнита: указан UI или дефолт = первый союзник кроме самого Фиксера.
  let target = null;
  if (opts.unitInstanceId != null) {
    for (const player of state.players) {
      for (const u of player.unitsOnField()) {
        if (u.instanceId === opts.unitInstanceId) { target = u; break; }
      }
      if (target) break;
    }
  } else {
    target = p.unitsOnField().find(u => u.instanceId !== fikser.instanceId) || null;
  }
  if (!target) {
    state.log.add(`🔧 ${fikser.name_ru} [${p.name}]: Приказ пропущен — нет юнита для движения`);
    return;
  }
  // Перемещаем по СВОЕЙ стороне юнита: своя сторона, между 1-й и 2-й линиями.
  const owner = state.players[target.ownerId];
  // Дефолт: на свободную ячейку другой линии.
  let destLine, destSlot;
  if (opts.line != null && opts.slot != null) {
    destLine = opts.line;
    destSlot = opts.slot;
  } else {
    const otherLine = 1 - target.line;
    let found = -1;
    for (let s = 0; s < 4; s++) {
      if (owner.field[otherLine][s] === null) { found = s; break; }
    }
    if (found < 0) {
      state.log.add(`🔧 ${fikser.name_ru} [${p.name}]: некуда передвинуть (другая линия полная)`);
      return;
    }
    destLine = otherLine;
    destSlot = found;
  }
  // Валидация: ячейка должна быть свободна и на стороне target's owner.
  if (owner.field[destLine][destSlot] !== null) {
    state.log.add(`🔧 ${fikser.name_ru} [${p.name}]: целевая ячейка занята — Приказ пропущен`);
    return;
  }
  // Снимаем юнита с прежнего слота.
  const prevLine = target.line;
  const prevSlot = target.slot;
  owner.field[prevLine][prevSlot] = null;
  // Ставим на новый слот, сохраняя ХП/Моды/Броню.
  owner.field[destLine][destSlot] = target;
  target.line = destLine;
  target.slot = destSlot;
  state.log.add(
    `🔧 ${fikser.name_ru} [${p.name}]: ${target.name_ru} [${owner.name}] ` +
    `${prevLine === 0 ? '1-я' : '2-я'}/${prevSlot + 1} → ${destLine === 0 ? '1-я' : '2-я'}/${destSlot + 1}`
  );
}
